import nl from 'date-fns/locale/nl';
import {ErrorMessage, useField} from 'formik';
import React from 'react';
import {registerLocale} from 'react-datepicker';
import {Icon} from '../../../../elements/Icon/Icon';
import {GenericDatePicker} from '../GenericDatePicker';
import {RawGenericDatePicker} from '../RawGenericDatePicker';
registerLocale('nl', nl);

import 'react-datepicker/dist/react-datepicker.css';
import {FormContainer} from '../../../form-containers/FormGroup';
import {FormErrorMessage} from '../../../form-labels/FormErrorMessage';

/**
 * @param {object} param0
 * @param {string} [param0.name]
 * Meant for formik Input
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name to a Formik DayPicker component is essential because Formik uses the name prop to link the value of the  DayPicker to the Formik state.
 * Note that the Formik type is the default version injected into the form control you are applying.
 *
 * @param {boolean} [param0.raw]
 * If set to true, a non-Formik version of the form control will be injected instead.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {string} [param0.placeholder]
 * The text displayed in the DayPicker when it is empty and not focused, providing a hint to the user about what information is expected.
 * Placeholder text follows a naming convention for consistency:
 *   - 'Select an option'
 *   - 'Pick an date'
 *
 * Avoid text like this:
 * --- date ----
 *
 *  @param {function} [param0.onChange]
 *  Set to a callback function with one argument, which stands for the date selected by the user. The function body should call the updater function returned by the useState hook to update the state.
 *
 * @param {function} [param0.selected]
 * set to the selected date, stored in the state. It is similar to value prop on **<input>** elements.
 *
 *  @param {array | object} [param0.excludeDateIntervals]
 * Set the value of the excludeDateIntervals prop to an array of objects with two properties: start and end. Each object represents an interval to be excluded from selection. Multiple intervals can be specified within the array. Dates falling outside of these intervals will be selectable.
 *
 * @param {boolean} [param0.showWeekNumbers=false]
 * Set to true to display week numbers alongside the calendar days in the datepicker. Week numbers indicate the week of the year each date falls into, providing additional context for users when selecting dates.
 *
 * @param {number} [param0.monthsShown=1]
 * Meant for non formik datepicker (Raw)
 * Specifies the number of months to display simultaneously in the datepicker. By default, only one month is shown. Increasing this value allows users to view and select dates from multiple months at once, which can be useful for tasks such as scheduling or planning over longer periods.
 *
 * @param {React.Component} [param0.customInput]
 * Meant for non formik datepicker (Raw)
 * Allows you to provide a custom input component to be used instead of the default input field. This gives you the flexibility to customize the appearance and behavior of the input field according to your application's needs.
 *
 * @param {string} [param0.locale="en-US"]
 * Meant for non formik datepicker (Raw)
 *  Sets the locale used by the datepicker for displaying dates and formatting. The value should be a string representing a valid locale identifier, such as "en-US" for English (United States). Providing the appropriate locale ensures that dates are displayed in the desired language and format for your users.
 *
 * @param {Date} [param0.maxDate]
 * Meant for non formik datepicker (Raw)
 * Sets the maximum selectable date in the datepicker, restricting date selection.
 *
 * @param {Date} [param0.minDate]
 * Meant for non formik datepicker (Raw)
 * Sets the minimum selectable date in the datepicker, restricting date selection.
 */

export function DayPicker({
    raw,
    onChange,
    name,
    disabled,
    selected,
    label,
    excludeDateIntervals,
    showWeekNumbers,
    validationError,
    customInput,
    monthsShown,
    locale,
    maxDate,
    minDate,
}) {
    let [, fieldMeta, fieldHelpers] = useField(name);
    return (
        <FormContainer
            disabled={disabled}
            errorMessage={
                raw ? (
                    <>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>
                ) : (
                    <ErrorMessage name={name} component={FormErrorMessage} />
                )
            }
            label={label}
            suffix={
                <>
                    <Icon name="calendarMonth" />
                </>
            }>
            {raw ? (
                <RawGenericDatePicker
                    showTimeSelect={false}
                    dateFormat={'dd MMM yyyy'}
                    showWeekNumbers={showWeekNumbers}
                    selected={selected}
                    onChange={onChange}
                    customInput={customInput}
                    monthsShown={monthsShown}
                    locale={locale}
                    maxDate={maxDate}
                    minDate={minDate}
                    excludeDateIntervals={excludeDateIntervals}
                    disabled={disabled}
                />
            ) : (
                <GenericDatePicker
                    name={name}
                    disabled={disabled}
                    showTimeSelect={false}
                    showWeekNumbers={false}
                    onKeyDown={(e) => {
                        e.preventDefault();
                    }}
                    selected={fieldMeta.value ? new Date(+fieldMeta.value) : null}
                    onChange={(v) => fieldHelpers.setValue(Date.parse(v))}
                    dateFormat={'dd MMM yyyy'}
                    excludeDateIntervals={excludeDateIntervals}
                />
            )}
        </FormContainer>
    );
}
